/**
 * meterMilestone — the meter's per-phase milestone bloom (W5 C4).
 *
 * The canvas dial is the instrument's protagonist, and before W5 it had
 * NO completion gesture per phase — only the big 4π whirl at full-run
 * completion. This composable gives each of the three measured phases a
 * small "got it" beat: when the needle first SETTLES within an active
 * phase (its `SmoothProgress` converges on the measured value), a
 * one-shot ring-glow bloom suffuses the active ring.
 *
 * It is the canvas-side member of the "affirmation" motion family — it
 * rhymes with the timeline's per-phase completion tick (W5-C1) and the
 * gold completion badge: a staged, self-drawing, one-shot acknowledgement.
 *
 * Precept 10 — the bloom envelope is a keyframes.js `NumericAnimation`.
 * Its `.play()` runs the library's managed rAF (the idiomatic
 * choreography engine, NOT a hand-rolled loop); the per-frame callback
 * writes the eased `intensity` into a plain holder the meter's paint
 * loop reads. The paint loop only SAMPLES — it never schedules the bloom.
 */

import { NumericAnimation } from "@mkbabb/keyframes.js";
import { easeOutCubic } from "@design";

/** The bloom envelope window. A brief, ease-out swell-and-fade. */
export const MILESTONE_BLOOM_MS = 420;

export interface MeterMilestone {
    /** 0→peak→0 bloom envelope the renderer composes into the ring paint. */
    readonly intensity: () => number;
    /**
     * Drive the milestone gate. Call once per frame from the meter's
     * paint loop with the active phase name and the needle's `settled`
     * flag. The first `settled === true` frame within a fresh phase
     * fires the bloom exactly once; the phase key resets the gate so the
     * next phase earns its own bloom.
     */
    observe(phase: string | undefined, settled: boolean): void;
    /** Cancel any in-flight bloom and clear the gate (run start / abort). */
    reset(): void;
}

/**
 * Construct the milestone-bloom controller. Stateless toward Vue — the
 * meter renderer owns it as a plain object, so it composes into the
 * `useMeterRenderer` shell without a second reactive surface.
 *
 * Renamed from `useMeterMilestone` per FA-2 §3.2 (AH.W5-b.2c): this
 * factory never calls a Vue composable primitive, so the `use*` prefix
 * was a naming promise it didn't keep.
 */
export function createMeterMilestone(): MeterMilestone {
    // The eased bloom value, written by the running NumericAnimation's
    // per-frame callback and sampled by the meter paint loop.
    let intensityValue = 0;
    // The phase whose milestone has already fired — the gate. `null`
    // means "this phase has not yet bloomed".
    let firedPhase: string | null = null;
    // Tracks the phase observed last frame so a phase change re-arms the
    // gate even if the needle is already settled in the new phase.
    let lastPhase: string | undefined;
    let play: NumericAnimation<{ i: number }> | null = null;

    function fire(): void {
        play?.stop();
        // Swell to a unit peak then fall back to 0 — `easeOutCubic`
        // gives the bloom a fast attack and a soft decay (the badge
        // aura's grammar at canvas scale). `respectReducedMotion` snaps
        // the envelope straight to its `{ i: 0 }` end-state, so under a
        // reduced-motion preference the bloom simply does not paint.
        const anim = new NumericAnimation<{ i: number }>(
            [{ i: 0 }, { i: 1 }, { i: 0 }],
            {
                duration: MILESTONE_BLOOM_MS,
                timingFunction: easeOutCubic,
                respectReducedMotion: true,
            },
        );
        play = anim;
        void anim
            .play(({ i }) => {
                intensityValue = i;
            })
            .then(() => {
                if (play === anim) {
                    intensityValue = 0;
                    play = null;
                }
            });
    }

    function observe(phase: string | undefined, settled: boolean): void {
        const isMeasured =
            phase === "ping" || phase === "download" || phase === "upload";

        // Phase changed → re-arm: the new phase has not bloomed yet.
        if (phase !== lastPhase) {
            lastPhase = phase;
            if (firedPhase !== phase) firedPhase = null;
        }

        if (!isMeasured) return;
        // First settled frame of an un-bloomed phase → one-shot bloom.
        if (settled && firedPhase !== phase) {
            firedPhase = phase ?? null;
            fire();
        }
    }

    function reset(): void {
        play?.stop();
        play = null;
        intensityValue = 0;
        firedPhase = null;
        lastPhase = undefined;
    }

    return {
        intensity: () => intensityValue,
        observe,
        reset,
    };
}
